import React, { useContext, useEffect, useRef, useState } from 'react'
import './style.scss'
import Icon from 'react-icons-kit'
import {
    activity, calendar, chevronLeft, chevronRight, eye, heart,
    image as imageIcon, mapPin, messageCircle, phoneOutgoing, send as sendIcon, users, zap
} from 'react-icons-kit/feather'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { io } from 'socket.io-client'
import { toast } from 'react-toastify'
import { UserContext } from '../../../utils/context/userContext'
import Requests from '../../../utils/Requests/Index'
import { HeightCalculate } from '../../../utils/helpers'
import { Images } from '../../../utils/Images'
import { mobileMemberSetter } from '../../../utils/Redux/Slice/Chat/SingleChatMemberSlice'
import ContactInfo from '../Modal/ContactInfo'
import ContactNumber from '../Modal/ContactNumber'
import ContactViewConfirm from '../Modal/ContactViewConfirm'

const ProfileHero = ({ item }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { user, handleRefetch } = useContext(UserContext)

    const [index, setIndex] = useState(0)
    const [isLoading, setLoading] = useState({ id: null, status: false })
    const [header] = useState({ headers: { Authorization: "Bearer " + localStorage.getItem('token') } })
    const [send, setSend] = useState(false)
    const [contactShow, setContactShow] = useState(false)
    const [contactModal, setContactModal] = useState(false)
    const [viewConfirmModal, setViewConfirmModal] = useState(false)
    const [contactNumberModal, setContactNumberModal] = useState({ show: false, number: '', numberType: '' })
    const [pendingType, setPendingType] = useState('call')
    const socket = useRef()

    useEffect(() => {
        socket.current = io(`${process.env.REACT_APP_API_URL}`, { transports: ["websocket", 'polling'] })
    }, [])

    // Already proposed/connected, and already unlocked contact
    useEffect(() => {
        if (user && item?._id) {
            const alreadyConnected = user?.connections?.find(info => info._id === item._id)
                || user?.sentRequests?.indexOf(item?._id) > -1
                || user?.pendingRequests?.indexOf(item?._id) > -1

            setSend(Boolean(alreadyConnected))
        }

        if (user?.profileType === "Paid" && user?.packageInfo?.contactViewed?.indexOf(item?._id) > -1) {
            setContactShow(true)
        }
    }, [user, item]) // eslint-disable-line react-hooks/exhaustive-deps

    // Send proposal
    const sendRequest = async id => {
        try {
            setLoading({ id: id, status: true })
            const response = await Requests.Connection.Connect(id, header)
            if (response) {
                toast.success(response.message)

                socket.current.emit("sendProposal", {
                    senderId: user?.userID,
                    name: user?.name,
                    receiverId: id
                })

                if (response.status) setSend(true)
                handleRefetch()
            }
            setLoading({ id: null, status: false })
        } catch (error) {
            if (error) console.log(error)
        }
    }

    // Confirm a contact view (uses one contact view from the package)
    const handleViewConfirm = async userId => {
        try {
            if (user?.packageInfo?.proposal?.contactViewLimit <= 0) {
                toast.warning("Your contact viewed limit finished!")
                return
            } else {
                setViewConfirmModal(false)
                setContactShow(true)
            }

            const response = await Requests.Profile.UpdateContactViewed(userId, header)
            if (response?.status) {
                toast.success(response.message)
                handleRefetch()
                setContactNumberModal({ show: true, numberType: pendingType, number: item?.phone })
            }
        } catch (error) {
            console.log(error)
        }
    }

    const openContact = numberType => {
        if (contactShow) {
            setContactNumberModal({ show: true, numberType, number: item?.phone })
        } else if (user?.profileType === "Paid") {
            setPendingType(numberType)
            setViewConfirmModal(true)
        } else {
            setContactModal(true)
        }
    }

    // Chat is a paid feature - free members get the upgrade modal
    const handleChat = () => {
        if (user?.profileType === "Free") {
            setContactModal(true)
            return
        }

        dispatch(mobileMemberSetter({
            _id: item?._id,
            name: item?.name,
            profileId: item?.profileId || null,
            birthCountry: item?.birthCountry || null,
            age: item?.age || null,
            materialStatus: item?.materialStatus || null,
            height: item?.height ? HeightCalculate(item.height) : null,
            bloodGroup: item?.bloodGroup || null,
            religion: item?.religion || null,
            city: item?.city || null,
            workingWith: item?.workingWith || "Not working",
            images: item?.images?.length ? [item.images[0]] : [{ name: Images.NoImage }]
        }))
        navigate('/messanger')
    }

    // Gallery
    const imageList = item?.images?.length ? item.images : [{ name: Images.NoImage }]

    const onPrevClick = () => {
        setIndex(index > 0 ? index - 1 : imageList.length - 1)
    }

    const onNextClick = () => {
        setIndex(index === imageList.length - 1 ? 0 : index + 1)
    }

    const onlineText = typeof item?.isOnline === 'string' && !item.isOnline.includes('null') ? item.isOnline : ''
    const online = onlineText.startsWith('online')
    const location = [item?.city, item?.livingCountry].filter(Boolean).join(', ')
    const background = [item?.religion, item?.socialOrder].filter(Boolean).join(' • ')

    return (
        <section className="sp-hero">

            {/* Gallery */}
            <div className="sp-hero-media">
                <div className="sp-hero-frame">
                    <img src={imageList[index]?.name || Images.NoImage} alt={item?.name || 'Profile'} />

                    {onlineText ?
                        <span className={`sp-online ${online ? 'is-on' : 'is-off'}`} title={onlineText}>
                            <i /> {online ? 'Online' : 'Offline'}
                        </span>
                        : null}

                    {imageList.length > 1 ?
                        <>
                            <button type="button" className="sp-gal prev" onClick={onPrevClick} aria-label="Previous photo">
                                <Icon icon={chevronLeft} size={18} />
                            </button>
                            <button type="button" className="sp-gal next" onClick={onNextClick} aria-label="Next photo">
                                <Icon icon={chevronRight} size={18} />
                            </button>
                            <span className="sp-gal-count">
                                <Icon icon={imageIcon} size={13} /> {index + 1} / {imageList.length}
                            </span>
                        </>
                        : null}
                </div>

                {imageList.length > 1 ?
                    <div className="sp-thumbs">
                        {imageList.map((info, i) =>
                            <button
                                type="button"
                                key={i}
                                className={i === index ? 'active' : ''}
                                onClick={() => setIndex(i)}
                                aria-label={`Photo ${i + 1}`}
                            >
                                <img src={info?.name || Images.NoImage} alt="" />
                            </button>
                        )}
                    </div>
                    : null}
            </div>

            {/* Identity + actions */}
            <div className="sp-hero-body">
                <h2 className="sp-hero-name">{item?.name || '—'}</h2>
                <p className="sp-hero-id">Profile ID: <span>{item?.profileId || '—'}</span></p>

                <div className="sp-hero-chips">
                    {item?.age ?
                        <span className="sp-chip"><Icon icon={calendar} size={13} /> {item.age} Years</span>
                        : null}
                    {item?.height ?
                        <span className="sp-chip"><Icon icon={activity} size={13} /> {HeightCalculate(item.height)}</span>
                        : null}
                    {location ?
                        <span className="sp-chip"><Icon icon={mapPin} size={13} /> {location}</span>
                        : null}
                    {item?.materialStatus ?
                        <span className="sp-chip"><Icon icon={users} size={13} /> {item.materialStatus}</span>
                        : null}
                    {background ?
                        <span className="sp-chip"><Icon icon={zap} size={13} /> {background}</span>
                        : null}
                </div>

                {item?.workingWith ?
                    <p className="sp-hero-work">
                        Works as <b>{item.workingWith}</b>{item?.professionArea ? ` in ${item.professionArea}` : ''}
                    </p>
                    : null}

                <div className="sp-hero-actions">
                    {!send ?
                        <button
                            type="button"
                            className="sp-btn sp-btn-primary"
                            disabled={isLoading.status && item?._id === isLoading.id}
                            onClick={() => sendRequest(item?._id)}
                        >
                            <Icon icon={heart} size={16} /> Propose Now
                        </button>
                        :
                        <>
                            <button type="button" className="sp-btn sp-btn-call" onClick={() => openContact('call')}>
                                <Icon icon={phoneOutgoing} size={15} /> Call
                            </button>
                            <button type="button" className="sp-btn sp-btn-wa" onClick={() => openContact('whatsapp')}>
                                <Icon icon={messageCircle} size={15} /> WhatsApp
                            </button>
                            <button type="button" className="sp-btn sp-btn-chat" onClick={handleChat}>
                                <Icon icon={sendIcon} size={15} /> Chat
                            </button>
                        </>
                    }
                </div>

                {!send ?
                    <p className="sp-hero-hint">
                        <Icon icon={heart} size={13} /> Send a proposal to unlock contact and chat options.
                    </p>
                    : user?.profileType !== "Paid" ?
                        <p className="sp-hero-hint">
                            <Icon icon={eye} size={13} />
                            <Link to="/my-dashboard/package"> Upgrade</Link> to call, chat and see contact details.
                        </p>
                        : null}
            </div>

            {/* Modals */}
            {contactModal ?
                <ContactInfo show={contactModal} onHide={() => setContactModal(false)} />
                : null}

            {viewConfirmModal ?
                <ContactViewConfirm
                    show={viewConfirmModal}
                    onHide={() => setViewConfirmModal(false)}
                    handleViewConfirm={() => handleViewConfirm(item?._id)}
                />
                : null}

            {contactNumberModal.show ?
                <ContactNumber
                    show={contactNumberModal.show}
                    numberType={contactNumberModal.numberType}
                    number={contactNumberModal.number}
                    onHide={() => setContactNumberModal({ ...contactNumberModal, show: false })}
                />
                : null}
        </section>
    )
}

export default ProfileHero
